// Chat/useWebSocket.ts
import {useEffect, useState} from 'react';
import useWebSocketLib from 'react-use-websocket';
import {axios} from 'Auth/axiosConfig';
import {isTokenExpiringSoon} from 'Utils/jwt';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import pprint from 'Utils/pprint'; // ваша функция логирования

interface UseWebSocketProps {
    roomId: string;                 // ID комнаты, к которой подключаемся
    isAuthenticated: boolean;       // Авторизован ли пользователь
    userId: number | null;          // ID пользователя (или null)
    onMessage: (data: any) => void; // Коллбэк на входящие WS-сообщения
}

export default function useWebSocket(
    {
        roomId,
        isAuthenticated,
        userId,
        onMessage
    }: UseWebSocketProps) {
    const {byResponse} = useErrorProcessing();
    const [socketUrl, setSocketUrl] = useState<string>('');

    useEffect(() => {
        const setupWebSocketUrl = async () => {
            if (!isAuthenticated || !roomId) return;
            try {
                let token: string | null = localStorage.getItem('access') || null;
                if (token && isTokenExpiringSoon(token)) {
                    try {
                        const response = await axios.post('/api/v1/token/refresh/', {
                            refresh: localStorage.getItem('refresh'),
                        });
                        localStorage.setItem('access', response.data.access);
                        token = response.data.access;
                    } catch (error) {
                        byResponse(error);
                        return;
                    }
                }
                const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
                const wsUrl = `${wsProtocol}://${window.location.host}/ws/chat/${roomId}/?token=${token}`;
                setSocketUrl(wsUrl);
                pprint(`WebSocket connecting to room: ${roomId}`);
            } catch (e) {
                pprint('Unexpected error connecting to WebSocket:', e);
                byResponse(e);
            }
        };
        setupWebSocketUrl();
    }, [roomId, isAuthenticated, byResponse]);

    const {sendMessage, lastMessage, readyState} = useWebSocketLib(socketUrl, {
        onOpen: () => {
            pprint(`WebSocket connected to room: ${roomId}`);
        },
        onMessage: (event: MessageEvent) => {
            const data = JSON.parse(event.data);
            pprint('WebSocket message received:', data);
            onMessage(data);
        },
        shouldReconnect: () => true,
        reconnectAttempts: Infinity,
        reconnectInterval: 2000,
    });

    return sendMessage;
}
